// useBilledMonths — an hourly building's last three billed months, before the
// management fee and tax (item 758, option A, Ben 2026-10-13).
//
// ONE read, through v_client_billing_lines_counted, which already counts each
// building-month from one source only (TCSD-0496) — so this card and the weekly
// reports agree, and no second rule per building is picked here. It runs with
// the viewer's own rights: billing lines are Finance's (executive, finance
// capability, superuser), so everyone else is told that in a sentence rather
// than shown "None yet" (TCSD-0049). Who may read them is NOT widened.
// The arithmetic is in ../lib/billed-months.ts.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { useReader } from '../../../shared/save-path';
import { summariseBilledMonths, type BilledMonthFigure, type CountedRow } from '../lib/billed-months';

export function useBilledMonths(facilityId: string, enabled: boolean, readable: boolean) {
  const [months, setMonths] = useState<BilledMonthFigure[]>([]);
  const [loading, setLoading] = useState(enabled && readable);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((n) => n + 1), []);
  useReader(['client_billing_lines'], refresh);

  useEffect(() => {
    if (!enabled || !readable) {
      setMonths([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const { data, error: readError } = await supabase
        .from('v_client_billing_lines_counted')
        .select('counted_month, line_amount, is_management_fee')
        .eq('facility_id', facilityId);
      if (cancelled) return;
      // ⛔ A refused read is not "None yet".
      if (readError) {
        setError(`The billed months could not be read: ${readError.message}`);
        setMonths([]);
      } else {
        setError(null);
        setMonths(summariseBilledMonths((data ?? []) as CountedRow[]));
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [facilityId, enabled, readable, tick]);

  return { months, loading, error };
}
